import { useEffect } from "react";
import useLocalStorageStateLib from "use-local-storage-state";

/**
 * A wrapper around use-local-storage-state that handles schema migrations.
 * This hook will ensure the local storage key-value pair has
 * all fields defined in the schema on read, using defaults for missing fields.
 * For primitive values, no migration is performed.
 *
 * @param key The localStorage key to use
 * @param options Options including defaultValue which acts as the schema for objects or default value for primitives
 * @returns A tuple of [value, setValue] similar to useState
 */
export function useLocalStorageState<T>(
  key: string,
  options: {
    defaultValue: T;
  }
): [T, (value: T | ((prevValue: T) => T)) => void] {
  const { defaultValue } = options;

  // Run migration when key changes
  useEffect(() => {
    // Skip migration for primitive default values
    if (
      typeof defaultValue !== "object" ||
      defaultValue === null ||
      Array.isArray(defaultValue)
    ) {
      return;
    }

    // Only migrate the specific key being used
    try {
      const storedValue = localStorage.getItem(key);
      if (storedValue) {
        const parsedStoredValue = JSON.parse(storedValue);

        // Skip migration if stored value is a primitive
        if (
          typeof parsedStoredValue !== "object" ||
          parsedStoredValue === null ||
          Array.isArray(parsedStoredValue)
        ) {
          return;
        }

        // Check if migration is needed
        let needsMigration = false;
        const migrated = { ...parsedStoredValue };

        // Check each field in the schema - only handle top-level fields
        Object.keys(defaultValue).forEach((field) => {
          if (!(field in parsedStoredValue)) {
            migrated[field] = (defaultValue as Record<string, any>)[field];
            needsMigration = true;
          }
        });

        // Update localStorage if migration was needed
        if (needsMigration) {
          localStorage.setItem(key, JSON.stringify(migrated));
        }
      }
    } catch (error) {
      console.error(`Failed to migrate localStorage key ${key}:`, error);
    }
  }, [key, defaultValue]);

  // Use the standard hook with the migrated data
  const [value, setValue] = useLocalStorageStateLib<T>(key, {
    defaultValue,
  });

  return [value, setValue];
}
